import CancelIcon from "@mui/icons-material/Clear";
import { Box, Button, IconButton, InputAdornment, Stack, TextField } from "@mui/material";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { makeStyles } from "@mui/styles";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { EMPLOYEE_ID } from "../../util/constants";
import { validateEmployeeId } from "../../util/inputValidation";
import { getWorkgroupGivenJobCode } from "../../util/workgroupMapper.js";
import { getEmployeeInBidGroup } from "../../util/bid/shiftBidUtils.js";
import { isDeletedBid, isAwardedBid } from "../../util/bid/bidCommon.js";

const useStyles = makeStyles((theme) => ({
  buttonStyle: {
    padding: "15px",
    marginLeft: "22px",
  },
  inputText: {
    width: "247px",
    textAlign: "left",
    marginLeft: "22px",
    background: theme.palette.secondary.swaWhite,
    "& .MuiFormHelperText-root.Mui-error": {
      backgroundColor: theme.palette.secondary.swaLightestBlue,
      margin: 0,
      paddingLeft: 10,
    },
  },
  employeeDataValue: {
    fontSize: "14px",
    marginTop: "-5px",
  },
  employeeDataTitle: {
    marginTop: "7px",
    color: theme.palette.secondary.swaGray,
  },
}));

const EmployeeBlockLineDetailPanelContent = (props) => {
  const classes = useStyles();
  const [employeeId, setEmployeeId] = useState("");
  const [employeeData, setEmployeeData] = useState(null);
  const [employeeIdErrors, setEmployeeIdErrors] = useState("");

  useEffect(() => {
    const { assignedEmployee } = props.bidLineRow;
    setEmployeeIdErrors("");
    if (assignedEmployee?.eid) {
      setEmployeeData(getEmployeeInBidGroup(assignedEmployee.eid, props.bidGroup));
      setEmployeeId(assignedEmployee.eid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.bidLines]);

  const clearAssignedEmployeeData = () => {
    const copiedBidLines = [...props.bidLines];
    const clickedBidlineIdx = copiedBidLines.findIndex((emp) => emp?.assignedEmployee?.eid === employeeData?.eid);
    if (copiedBidLines[clickedBidlineIdx]?.assignedEmployee) {
      copiedBidLines[clickedBidlineIdx].assignedEmployee = null;
    }
    props.setBidLines(copiedBidLines);
  };

  const handleEmployeeIdChange = (event) => {
    if (event?.target?.value === "") {
      clearAssignedEmployeeData();
      setEmployeeData(null);
    }
    setEmployeeIdErrors("");
    setEmployeeId(event?.target?.value?.trim()?.toLowerCase());
  };

  const clearEmployeeData = () => {
    setEmployeeId("");
    setEmployeeData(null);
    setEmployeeIdErrors("");
    const errors = validateEmployeeId("");
    if (errors) {
      setEmployeeIdErrors(errors);
    }
    clearAssignedEmployeeData();
  };

  const handleEmployeeDataClear = () => {
    clearEmployeeData();
  };

  const handleEmployeeIdBlur = (event) => {
    if (event?.target?.value !== "") {
      handleAssign(event.target.value);
    }
  };

  const handleEmployeeIdEnterKeyPressed = (event) => {
    if (event.key === "Enter") {
      handleEmployeeIdBlur(event);
    }
  };

  const handleAssign = (eid) => {
    const errors = validateEmployeeId(eid);
    if (!errors) {
      if (eid === props.bidLineRow?.assignedEmployee?.eid) {
        setEmployeeIdErrors("");
        setEmployeeData(getEmployeeInBidGroup(eid, props.bidGroup));
      } else {
        assignEmployeeToBidLine();
      }
    } else {
      setEmployeeIdErrors(errors);
    }
  };

  const assignEmployeeToBidLine = () => {
    const selectedEmployee = props.bidGroup.filter((emp) => emp.eid === employeeId);
    const isEmployeeAlreadyAssignedWithBlockLine = props.bidLines.findIndex(
      (emp) => emp?.assignedEmployee?.eid === employeeId
    );
    if (selectedEmployee.length === 0 || selectedEmployee[0].isRemoved) {
      setEmployeeIdErrors("Employee not in Bid Group or Invalid Employee ID");
    } else if (!selectedEmployee[0].isEligible) {
      setEmployeeIdErrors("Employee is ineligible");
    } else if (props.bidLineRow.ftpt !== selectedEmployee[0].ftpt) {
      setEmployeeIdErrors(`Must select ${props.bidLineRow.ftpt} employee`);
    } else if (selectedEmployee.length > 0 && isEmployeeAlreadyAssignedWithBlockLine === -1) {
      setEmployeeIdErrors("");
      const copiedBidLines = [...props.bidLines];
      const clickedBidlineIdx = copiedBidLines.findIndex(
        (bidline) => bidline?.lineNumber === props.bidLineRow.lineNumber
      );
      if (clickedBidlineIdx !== -1) {
        copiedBidLines[clickedBidlineIdx].assignedEmployee = {};
        copiedBidLines[clickedBidlineIdx].assignedEmployee.eid = selectedEmployee[0].eid;
        copiedBidLines[clickedBidlineIdx].assignedEmployee.firstName = selectedEmployee[0].shortName;
        copiedBidLines[clickedBidlineIdx].assignedEmployee.lastName = selectedEmployee[0].lastName;
        setEmployeeData(selectedEmployee[0]);
        props.setBidLines(copiedBidLines);
      }
    } else {
      if (employeeId !== props.bidLineRow?.assignedEmployee?.eid) {
        setEmployeeIdErrors("Employee is already assigned a line");
      } else {
        setEmployeeIdErrors("");
      }
    }
  };

  return (
    <Stack direction="column" sx={{ paddingTop: 1.5, paddingBottom: 1, backgroundColor: "secondary.swaLightestBlue" }}>
      <Grid container textAlign="left" alignItems="center" spacing={2}>
        <Grid item md={4}>
          <TextField
            className={classes.inputText}
            label={EMPLOYEE_ID}
            variant="outlined"
            value={employeeId}
            onChange={handleEmployeeIdChange}
            onBlur={handleEmployeeIdBlur}
            onKeyUp={handleEmployeeIdEnterKeyPressed}
            InputProps={{
              endAdornment: employeeId && (
                <InputAdornment position="start">
                  <IconButton
                    size="small"
                    className="cancel-icon-button"
                    onMouseDown={handleEmployeeDataClear}
                    disabled={
                      isDeletedBid(props.bidStatus) || isAwardedBid(props.bidStatus) || props.bidLineRow?.isDupedLine
                    }
                  >
                    <CancelIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            disabled={isDeletedBid(props.bidStatus) || isAwardedBid(props.bidStatus) || props.bidLineRow?.isDupedLine}
            error={!!employeeIdErrors}
            helperText={employeeIdErrors}
          />
          <Button
            className={classes.buttonStyle}
            variant="contained"
            color="primary"
            size="small"
            disabled={isDeletedBid(props.bidStatus) || isAwardedBid(props.bidStatus) || props.bidLineRow?.isDupedLine}
            onClick={() => handleAssign(employeeId)}
          >
            ASSIGN
          </Button>
        </Grid>
        {employeeData?.eid && (
          <Grid item md={8}>
            <Grid container spacing={2} alignItems="center" alignContent="center">
              <Grid item xs={2} sx={{ fontSize: "16px", paddingLeft: "16px", textAlign: "left" }}>
                {`${employeeData.lastName}, ${employeeData.shortName}`}
              </Grid>
              <Grid item xs={10} sx={{ textAlign: "left" }}>
                <Stack direction="row">
                  <Box ml={2} mr={2}>
                    <Typography className={classes.employeeDataTitle} variant="overline" component="span">
                      HOME STATION
                    </Typography>
                    <p className={classes.employeeDataValue}>{employeeData?.homeStation}</p>
                  </Box>
                  <Box ml={2} mr={2}>
                    <Typography className={classes.employeeDataTitle} variant="overline" component="span">
                      WORKGROUP
                    </Typography>
                    <p className={classes.employeeDataValue}>
                      {getWorkgroupGivenJobCode(employeeData.homeAccountJobCode)}
                    </p>
                  </Box>
                  <Box ml={2} mr={2}>
                    <Typography className={classes.employeeDataTitle} variant="overline" component="span">
                      STATUS
                    </Typography>
                    <p className={classes.employeeDataValue}>{employeeData?.ftpt}</p>
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Stack>
  );
};

EmployeeBlockLineDetailPanelContent.propTypes = {
  bidLineRow: PropTypes.object,
  bidLines: PropTypes.array,
  bidGroup: PropTypes.array,
  setBidLines: PropTypes.func.isRequired,
  bidStatus: PropTypes.string.isRequired,
};

export default EmployeeBlockLineDetailPanelContent;
